import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPinned, BedDouble, BookOpen, Bookmark, Loader2 } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { SectionLabel } from '../components/shared';
import { fetchItinerary } from '../api';

const TYPE_META = {
  emirate: { icon: MapPinned, label: 'Emirates' },
  hotel: { icon: BedDouble, label: 'Hotels' },
  guide: { icon: BookOpen, label: 'Guides' },
};

const SharedPlanner = () => {
  const { sid } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useReveal([items.length, loading]);

  useEffect(() => {
    fetchItinerary(sid)
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [sid]);

  const grouped = items.reduce((acc, it) => {
    (acc[it.type] = acc[it.type] || []).push(it);
    return acc;
  }, {});
  const order = ['emirate', 'hotel', 'guide'];

  return (
    <div className="bg-[#F6F1E3] min-h-[70vh]">
      <section className="bg-[#1B1D21]">
        <div className="max-w-5xl mx-auto px-5 py-16 text-center">
          <span className="text-[12px] font-semibold tracking-[0.18em] uppercase text-[#CBA344]">Shared trip</span>
          <h1 className="mt-3 font-display text-[40px] sm:text-[48px] leading-[1.08] font-semibold text-white">
            A shared UAE trip plan
          </h1>
          <p className="mt-4 text-[18px] text-[#c3d1de]">
            Someone shared the emirates, hotels and guides they&apos;re planning around.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 py-14">
        {loading ? (
          <div className="py-20 flex justify-center text-[#92722A]"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 rounded-2xl border border-dashed border-[#D7BC6D] bg-[#EFE7D2]">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F2ECCF] text-[#92722A]">
              <Bookmark className="w-7 h-7" />
            </span>
            <h2 className="mt-5 font-display text-[26px] font-semibold text-[#1B1D21]">This shared plan is empty</h2>
            <p className="mt-3 text-[16px] text-[#4B4F58]">There&apos;s nothing saved in this trip yet.</p>
            <Link to="/emirates" className="mt-6 inline-block btn-sunset rounded-full px-6 py-3 text-[15px] font-semibold">Explore the emirates</Link>
          </div>
        ) : (
          <div className="space-y-12">
            {order.filter((t) => grouped[t]?.length).map((type) => {
              const Meta = TYPE_META[type];
              const Icon = Meta.icon;
              return (
                <section key={type} className="reveal">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5 text-[#e0564e]" />
                    <SectionLabel>{Meta.label}</SectionLabel>
                    <span className="text-[13px] text-[#8a7a63]">({grouped[type].length})</span>
                  </div>
                  <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    {grouped[type].map((it) => (
                      <Link key={it.id} to={it.to} className="lift block rounded-2xl bg-white border border-[#E6D7A2] p-5 shadow-[0_14px_34px_-26px_rgba(27,29,33,0.5)]">
                        <h3 className="font-display text-[19px] font-semibold text-[#1B1D21]">{it.title}</h3>
                        {it.subtitle ? <p className="mt-1 text-[14px] text-[#4B4F58]">{it.subtitle}</p> : null}
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })}
            <div className="pt-4 text-center">
              <Link to="/" className="btn-outline-ink inline-block rounded-full px-6 py-3 text-[15px] font-semibold">Plan your own trip →</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedPlanner;
